import { toolboxApi, unwrapResult } from '~/modules/query/base';
import type {
  Attribute,
  AttributeInput,
  DashboardFlags,
  DashboardRequests,
  DashboardUpdates,
  Environment,
  Explanation,
  FeatureRequestDetail,
  Flag,
  FlagContext,
  FlagDefinition,
  FlagDetail,
  FlagInput,
  KnownUser,
  ModerateInput,
  Product,
  ProductUpdate,
  RequestListParams,
  RequestPage,
  SdkKey,
  UpdateInput,
  UserListParams,
  UsersOverview,
  VisibleUpdate,
} from '~/components/pages/feature-flags/types';

const BASE = '/feature-flags';

const qs = (params?: Record<string, string | number | undefined>): string => {
  const usable = Object.entries(params || {}).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  return usable.length
    ? `?${new URLSearchParams(usable.map(([k, v]) => [k, String(v)])).toString()}`
    : '';
};

const seg = encodeURIComponent;

/** What the product drawer's preview asks the service to do; the write actions change stored choices. */
const PREVIEW_WRITE_ACTIONS = new Set(['create', 'vote', 'enhance', 'update-choice']);

export interface DashboardArgs {
  environment: string;
  product?: string;
}

export interface RequestArgs {
  environment: string;
  product: string;
  body: { action: string } & Record<string, unknown>;
}

export const featureFlagsApi = toolboxApi.injectEndpoints({
  endpoints: (builder) => ({
    // Environments
    listEnvironments: builder.query<Environment[], void>({
      query: () => ({ url: `${BASE}/environments`, method: 'GET' }),
      transformResponse: unwrapResult<Environment[]>,
      providesTags: ['Environment'],
    }),
    createEnvironment: builder.mutation<{ key: string }, Environment>({
      query: (data) => ({ url: `${BASE}/environments`, method: 'POST', data }),
      transformResponse: unwrapResult<{ key: string }>,
      invalidatesTags: ['Environment'],
    }),
    renameEnvironment: builder.mutation<
      unknown,
      { key: string; data: { name: string; expectedName: string } }
    >({
      query: ({ key, data }) => ({ url: `${BASE}/environments/${seg(key)}`, method: 'PUT', data }),
      invalidatesTags: ['Environment'],
    }),
    deleteEnvironment: builder.mutation<
      unknown,
      { key: string; data: { expectedName: string; confirmation: string } }
    >({
      query: ({ key, data }) => ({
        url: `${BASE}/environments/${seg(key)}`,
        method: 'DELETE',
        data,
      }),
      // Deleting an environment also drops its flag settings, update settings, users and SDK keys.
      invalidatesTags: ['Environment', 'Flag', 'Update', 'FlagUser', 'SdkKey', 'FlagDashboard'],
    }),

    // Attributes
    listAttributes: builder.query<Attribute[], void>({
      query: () => ({ url: `${BASE}/attributes`, method: 'GET' }),
      transformResponse: unwrapResult<Attribute[]>,
      providesTags: ['Attribute'],
    }),
    createAttribute: builder.mutation<{ key: string }, AttributeInput>({
      query: (data) => ({ url: `${BASE}/attributes`, method: 'POST', data }),
      transformResponse: unwrapResult<{ key: string }>,
      invalidatesTags: ['Attribute'],
    }),
    updateAttribute: builder.mutation<
      unknown,
      { key: string; data: AttributeInput & { version: number } }
    >({
      query: ({ key, data }) => ({ url: `${BASE}/attributes/${seg(key)}`, method: 'PUT', data }),
      invalidatesTags: ['Attribute'],
    }),
    deleteAttribute: builder.mutation<unknown, { key: string; version: number }>({
      query: ({ key, version }) => ({
        url: `${BASE}/attributes/${seg(key)}${qs({ version })}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Attribute'],
    }),

    // Products
    listProducts: builder.query<Product[], void>({
      query: () => ({ url: `${BASE}/products`, method: 'GET' }),
      transformResponse: unwrapResult<Product[]>,
      providesTags: ['Product'],
    }),
    saveProduct: builder.mutation<{ key: string }, { data: Product; existing?: boolean }>({
      query: ({ data, existing = false }) =>
        existing
          ? { url: `${BASE}/products/${seg(data.key)}`, method: 'PUT', data }
          : { url: `${BASE}/products`, method: 'POST', data },
      transformResponse: unwrapResult<{ key: string }>,
      invalidatesTags: ['Product'],
    }),
    deleteProduct: builder.mutation<unknown, string>({
      query: (key) => ({ url: `${BASE}/products/${seg(key)}`, method: 'DELETE' }),
      invalidatesTags: ['Product'],
    }),

    // Flags
    listFlags: builder.query<Flag[], void>({
      query: () => ({ url: `${BASE}/flags`, method: 'GET' }),
      transformResponse: unwrapResult<Flag[]>,
      providesTags: ['Flag'],
    }),
    getFlag: builder.query<FlagDetail, string>({
      query: (key) => ({ url: `${BASE}/flags/${seg(key)}`, method: 'GET' }),
      transformResponse: unwrapResult<FlagDetail>,
      providesTags: ['Flag'],
    }),
    createFlag: builder.mutation<{ key: string }, FlagInput>({
      query: (data) => ({ url: `${BASE}/flags`, method: 'POST', data }),
      transformResponse: unwrapResult<{ key: string }>,
      invalidatesTags: ['Flag', 'FlagDashboard'],
    }),
    updateFlag: builder.mutation<
      { version: number },
      { key: string; data: FlagInput & { version: number } }
    >({
      query: ({ key, data }) => ({ url: `${BASE}/flags/${seg(key)}`, method: 'PUT', data }),
      transformResponse: unwrapResult<{ version: number }>,
      invalidatesTags: ['Flag', 'FlagDashboard'],
    }),
    setFlagEnabled: builder.mutation<
      unknown,
      { key: string; data: { environment: string; enabled: boolean } }
    >({
      query: ({ key, data }) => ({
        url: `${BASE}/flags/${seg(key)}/enabled`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Flag', 'FlagDashboard'],
    }),
    archiveFlag: builder.mutation<unknown, string>({
      query: (key) => ({ url: `${BASE}/flags/${seg(key)}/archive`, method: 'POST' }),
      invalidatesTags: ['Flag', 'FlagDashboard'],
    }),
    // The live tester runs on every edit: a POST, so it is never cached (`action` is the request body).
    explainFlag: builder.mutation<Explanation, { flag: FlagDefinition; context: FlagContext }>({
      query: (data) => ({ url: `${BASE}/flags/explain`, method: 'POST', data }),
      transformResponse: unwrapResult<Explanation>,
    }),

    // Product updates
    listUpdates: builder.query<ProductUpdate[], void>({
      query: () => ({ url: `${BASE}/updates`, method: 'GET' }),
      transformResponse: unwrapResult<ProductUpdate[]>,
      providesTags: ['Update'],
    }),
    getUpdate: builder.query<ProductUpdate, string>({
      query: (id) => ({ url: `${BASE}/updates/${seg(id)}`, method: 'GET' }),
      transformResponse: unwrapResult<ProductUpdate>,
      providesTags: ['Update'],
    }),
    createUpdate: builder.mutation<{ id: string }, UpdateInput>({
      query: (data) => ({ url: `${BASE}/updates`, method: 'POST', data }),
      transformResponse: unwrapResult<{ id: string }>,
      invalidatesTags: ['Update', 'FlagDashboard'],
    }),
    updateUpdate: builder.mutation<
      { version: number },
      { id: string; data: UpdateInput & { version: number } }
    >({
      query: ({ id, data }) => ({ url: `${BASE}/updates/${seg(id)}`, method: 'PUT', data }),
      transformResponse: unwrapResult<{ version: number }>,
      invalidatesTags: ['Update', 'FlagDashboard'],
    }),
    archiveUpdate: builder.mutation<unknown, string>({
      query: (id) => ({ url: `${BASE}/updates/${seg(id)}/archive`, method: 'POST' }),
      invalidatesTags: ['Update', 'FlagDashboard'],
    }),

    // Feature requests
    listRequests: builder.query<RequestPage, RequestListParams>({
      query: (params) => ({ url: `${BASE}/requests${qs({ ...params })}`, method: 'GET' }),
      transformResponse: unwrapResult<RequestPage>,
      providesTags: ['Request'],
    }),
    getRequest: builder.query<FeatureRequestDetail, { id: string; product: string }>({
      query: ({ id, product }) => ({
        url: `${BASE}/requests/${seg(id)}${qs({ product })}`,
        method: 'GET',
      }),
      transformResponse: unwrapResult<FeatureRequestDetail>,
      providesTags: ['Request'],
    }),
    moderateRequest: builder.mutation<unknown, { id: string; data: ModerateInput }>({
      query: ({ id, data }) => ({
        url: `${BASE}/requests/${seg(id)}/moderate`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Request', 'FlagDashboard'],
    }),

    // SDK keys
    listSdkKeys: builder.query<SdkKey[], void>({
      query: () => ({ url: `${BASE}/sdk-keys`, method: 'GET' }),
      transformResponse: unwrapResult<SdkKey[]>,
      providesTags: ['SdkKey'],
    }),
    createSdkKey: builder.mutation<{ token: string }, { name: string; environment: string }>({
      query: (data) => ({ url: `${BASE}/sdk-keys`, method: 'POST', data }),
      transformResponse: unwrapResult<{ token: string }>,
      invalidatesTags: ['SdkKey'],
    }),
    revokeSdkKey: builder.mutation<unknown, string>({
      query: (id) => ({ url: `${BASE}/sdk-keys/${seg(id)}`, method: 'DELETE' }),
      invalidatesTags: ['SdkKey'],
    }),

    // Known users
    listFlagUsers: builder.query<UsersOverview, UserListParams>({
      query: (params) => ({ url: `${BASE}/users${qs({ ...params })}`, method: 'GET' }),
      transformResponse: unwrapResult<UsersOverview>,
      providesTags: ['FlagUser'],
    }),
    getFlagUser: builder.query<KnownUser, string>({
      query: (id) => ({ url: `${BASE}/users/${seg(id)}`, method: 'GET' }),
      transformResponse: unwrapResult<KnownUser>,
      providesTags: ['FlagUser'],
    }),
    createFlagUser: builder.mutation<unknown, { environment: string; context: FlagContext }>({
      query: (data) => ({ url: `${BASE}/users`, method: 'POST', data }),
      invalidatesTags: ['FlagUser', 'FlagDashboard'],
    }),
    updateFlagUser: builder.mutation<
      unknown,
      { id: string; data: { environment: string; context: FlagContext } }
    >({
      query: ({ id, data }) => ({ url: `${BASE}/users/${seg(id)}`, method: 'PUT', data }),
      invalidatesTags: ['FlagUser'],
    }),

    // Dashboard
    getDashboardFlags: builder.query<DashboardFlags, DashboardArgs>({
      query: ({ environment, product }) => ({
        url: `${BASE}/dashboard/flags${qs({ environment, product })}`,
        method: 'GET',
      }),
      transformResponse: unwrapResult<DashboardFlags>,
      providesTags: ['FlagDashboard'],
    }),
    getDashboardUpdates: builder.query<DashboardUpdates, DashboardArgs>({
      query: ({ environment, product }) => ({
        url: `${BASE}/dashboard/updates${qs({ environment, product })}`,
        method: 'GET',
      }),
      transformResponse: unwrapResult<DashboardUpdates>,
      providesTags: ['FlagDashboard'],
    }),
    getDashboardRequests: builder.query<DashboardRequests, { product?: string }>({
      query: ({ product }) => ({
        url: `${BASE}/dashboard/requests${qs({ product })}`,
        method: 'GET',
      }),
      transformResponse: unwrapResult<DashboardRequests>,
      providesTags: ['FlagDashboard'],
    }),

    // Preview: the same dispatcher the customer drawer calls, under the admin's own identity.
    getPreviewUpdates: builder.query<
      { updates: VisibleUpdate[]; values: Record<string, boolean> },
      { environment: string; product: string }
    >({
      query: ({ environment, product }) => ({
        url: `${BASE}/preview${qs({ environment, product })}`,
        method: 'POST',
        data: { action: 'updates' },
      }),
      transformResponse: unwrapResult<{
        updates: VisibleUpdate[];
        values: Record<string, boolean>;
      }>,
      providesTags: ['FlagDashboard'],
    }),
    // Arbitrary preview actions (list/detail/compare/create/vote/enhance/update-choice) are never cached:
    // a mutation returns the service's `{result}` for the one call and stores nothing.
    previewAction: builder.mutation<unknown, RequestArgs>({
      query: ({ environment, product, body }) => ({
        url: `${BASE}/preview${qs({ environment, product })}`,
        method: 'POST',
        data: body,
      }),
      transformResponse: unwrapResult<unknown>,
      invalidatesTags: (result, error, { body }) =>
        PREVIEW_WRITE_ACTIONS.has(body.action) ? ['Request'] : [],
    }),
  }),
});

export const {
  useListEnvironmentsQuery,
  useCreateEnvironmentMutation,
  useRenameEnvironmentMutation,
  useDeleteEnvironmentMutation,
  useListAttributesQuery,
  useCreateAttributeMutation,
  useUpdateAttributeMutation,
  useDeleteAttributeMutation,
  useListProductsQuery,
  useSaveProductMutation,
  useDeleteProductMutation,
  useListFlagsQuery,
  useGetFlagQuery,
  useCreateFlagMutation,
  useUpdateFlagMutation,
  useSetFlagEnabledMutation,
  useArchiveFlagMutation,
  useExplainFlagMutation,
  useListUpdatesQuery,
  useGetUpdateQuery,
  useCreateUpdateMutation,
  useUpdateUpdateMutation,
  useArchiveUpdateMutation,
  useListRequestsQuery,
  useGetRequestQuery,
  useModerateRequestMutation,
  useListSdkKeysQuery,
  useCreateSdkKeyMutation,
  useRevokeSdkKeyMutation,
  useListFlagUsersQuery,
  useGetFlagUserQuery,
  useCreateFlagUserMutation,
  useUpdateFlagUserMutation,
  useGetDashboardFlagsQuery,
  useGetDashboardUpdatesQuery,
  useGetDashboardRequestsQuery,
  useGetPreviewUpdatesQuery,
  usePreviewActionMutation,
} = featureFlagsApi;
